﻿import { useEffect, useRef } from 'react';

import { SKUGMetrixTable } from 'pages/sku_management/components/SKUGMetrixTable';
import { IntlProvider, LocalizationProvider } from '@progress/kendo-react-intl';
import { getStringFromURL, setStringParamInUrl } from 'utils/other';
import NewSKUForm from 'pages/sku_management/components/NewSKUForm';
import { useSKUManagement } from 'pages/sku_management/context';
import { SKU, SKUApi, SKUSortField } from 'apis/SKUApi';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { PageWithHeader } from 'components/layout';
import { SortDescriptor } from 'utils/dataQuery';
import { KendoIntlProvider } from 'app/culture';
import { useNavigate } from 'react-router-dom';
import { Card, Modal } from 'react-bootstrap';
import { PageTitle } from 'utils/pageTitle';
import { useSuperState } from 'hooks';

export const SKUManagement = () => {

    const { formatMessage, locale } = useIntl();

    const initialSearchTerm = getStringFromURL('searchTerm') ?? '';

    const [state, setState] = useSuperState({
        loadingGridData: true,
        paginationSkip: 0,
        paginationTake: 15,
        searchTerm: initialSearchTerm,
        selectedSKUs: [] as SKU[],
        serverTotalCount: 0,
        showNewSkuModal: false,
        skus: [] as SKU[],
        sortDir: '',
        sortField: '' as SKUSortField | ''
    });

    const { loadingGridData, paginationSkip, paginationTake, searchTerm,
        selectedSKUs, serverTotalCount, showNewSkuModal, skus, sortDir, sortField } = state;

    const { permissions } = useSKUManagement();
    const { createToast } = useNotification();
    const navigate = useNavigate();

    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isInitialSearchRef = useRef(true);

    useEffect(() => {

        fetchSKUsPaged(0, paginationTake, '', '', initialSearchTerm);

    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Debounce search term changes for server-side pagination (1s delay)
    useEffect(() => {

        if (isInitialSearchRef.current) {

            isInitialSearchRef.current = false;
            return;

        }

        if (searchDebounceRef.current) {

            clearTimeout(searchDebounceRef.current);

        }
        searchDebounceRef.current = setTimeout(() => {

            setStringParamInUrl('searchterm', searchTerm);
            setState({ paginationSkip: 0 });
            fetchSKUsPaged(0, paginationTake, sortField, sortDir, searchTerm);

        }, 1000);

        return () => {

            if (searchDebounceRef.current) {

                clearTimeout(searchDebounceRef.current);

            }

        };

    }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchSKUsPaged = async (
        skip = paginationSkip,
        take = paginationTake,
        sField: SKUSortField | '' = sortField,
        sDir = sortDir,
        search = searchTerm
    ) => {

        setState({ loadingGridData: true });
        const page = Math.floor(skip / take) + 1;

        try {

            const result = await SKUApi.GetAllPaged(page, take, sField, sDir, search);
            setState({ serverTotalCount: result.TotalCount, skus: result.Items });

        } catch (e) {

            console.error('SKUApi.GetAllPaged failed:', e);
            console.error('Serialized:', JSON.stringify(e, null, 2));
            const message = typeof e === 'string' ? e : (e as any)?.message || (e as any)?.Message || '';
            if (message.toLowerCase().includes('timed out') || message.toLowerCase().includes('timeout')) {

                createToast(formatMessage({ id: 'the_request_timed_out_please_try_again.93e77e8d', defaultMessage: 'The request timed out. Please try again.' }), 'danger');

            } else {

                createToast(formatMessage({ id: 'failed_to_load_sku_data_please_try_again.9d4fc9c5', defaultMessage: 'Failed to load SKU data. Please try again.' }), 'danger');

            }

        } finally {

            setState({ loadingGridData: false });

        }

    };

    const handleServerPageChange = (skip: number, take: number) => {

        setState({ paginationSkip: skip, paginationTake: take });
        fetchSKUsPaged(skip, take, sortField, sortDir, searchTerm);

    };

    const handleServerSortChange = (sort: SortDescriptor[]) => {

        const newSortField = (sort.length > 0 ? sort[0].field : '') as SKUSortField | '';
        const newSortDir = sort.length > 0 ? (sort[0].dir ?? '') : '';
        setState({ paginationSkip: 0, sortDir: newSortDir, sortField: newSortField });
        fetchSKUsPaged(0, paginationTake, newSortField, newSortDir, searchTerm);

    };

    return (
        <KendoIntlProvider>
            <LocalizationProvider language={locale}>
                <IntlProvider locale={locale}>
                    <PageTitle title={`${formatMessage({ id: 'sku_management.6a91ee80', defaultMessage: 'SKU Management' })} | GMetrix`} />
                    <PageWithHeader
                        headerConfig={{
                            cta: {
                                hidden: permissions?.Edit === false,
                                label: (
                                    <div className={'d-inline-flex align-items-center gap-1'}>
                                        <i className={'fa-solid fa-plus'} />
                                        <span>
                                            <FormattedMessage defaultMessage="New SKU" id="new_sku.dee2389b" />
                                        </span>
                                    </div>
                                ),
                                onClick: () => setState({ showNewSkuModal: true })
                            },
                            title: formatMessage({ id: 'sku_management.6a91ee80', defaultMessage: 'SKU Management' })
                        }}
                    >
                        <div className='sku-management d-flex flex-column gap-3'>
                            <Card body>
                                <SKUGMetrixTable
                                    serverSidePagination
                                    columnData={[
                                        { field: 'Name', sortField: 'Name', title: formatMessage({ id: 'name.5458f379', defaultMessage: 'Name' }) },
                                        { field: 'AdditionalIdentifier', sortField: 'AdditionalIdentifier', title: formatMessage({ id: 'additional_identifier.74c49804', defaultMessage: 'Additional Identifier' }) },
                                        { field: 'CreatedDateString', sortField: 'CreateDate', title: formatMessage({ id: 'date_created.85efe6f3', defaultMessage: 'Date Created' }) },
                                        { field: 'CreatedBy', sortField: 'CreatedBy', title: formatMessage({ id: 'created_by.a458757d', defaultMessage: 'Created By' }) }
                                    ]}
                                    loadingGridData={loadingGridData}
                                    onDetailsItemClick={async dataItem => navigate('/sku-management/' + dataItem.Id, { state: { title: dataItem.Name } })}
                                    onSelectedChange={(val: any) => setState({ selectedSKUs: val })}
                                    onServerPageChange={handleServerPageChange}
                                    onServerSortChange={handleServerSortChange}
                                    searchTerm={searchTerm}
                                    selectableRows={false}
                                    selectedSKUs={selectedSKUs}
                                    serverTotalCount={serverTotalCount}
                                    setSearchTerm={(val: any) => setState({ searchTerm: val })}
                                    skus={skus}
                                    toggleColumns={true}
                                />
                            </Card>
                        </div>
                    </PageWithHeader>

                    <Modal
                        onHide={() => setState({ showNewSkuModal: false })}
                        show={showNewSkuModal}
                        size="xl"
                    >
                        <Modal.Header closeButton>
                            <Modal.Title className="w-100">
                                <FormattedMessage defaultMessage="New SKU" id="new_sku.dee2389b" />
                            </Modal.Title>
                        </Modal.Header>
                        <NewSKUForm
                            onSuccess={(primaryExternalId, skuName) => {

                                setState({ showNewSkuModal: false });
                                createToast(formatMessage({ id: 'sku_created_successfully.426db772', defaultMessage: 'SKU created successfully.' }), 'success');
                                if (primaryExternalId > 0) {

                                    navigate('/sku-management/' + primaryExternalId, { state: { title: skuName } });

                                }

                            }}
                            ActionsWrapper={Modal.Footer}
                            ContentWrapper={Modal.Body}
                            onClose={() => setState({ showNewSkuModal: false })}
                        />
                    </Modal>
                </IntlProvider>
            </LocalizationProvider>
        </KendoIntlProvider>
    );

};
